/* Anmeldung, Sitzung und Rolle.
 *
 * Der Zustand liegt im Modulbereich und wird von allen Komponenten geteilt
 * (Singleton). Kein Pinia — für diesen Umfang reicht das.
 */

import { ref, computed, watch } from 'vue';
import { supabase, isConfigured, INITIAL_HASH } from '../supabase/client.js';
import { clearUrlCache } from '../supabase/storage.js';
import { submitAccessRequest } from '../supabase/requests.js';
import { normalizeAccessRequest } from '../domain/accessRequest.js';
import { isEmailRateLimit } from '../domain/authErrors.js';
import { parseAuthLink } from '../domain/authLink.js';
import { useProfile } from './useProfile.js';
import {
  can as canDo,
  canDeleteFiles as canDeleteFilesFor,
  canDeletePlayers as canDeletePlayersFor,
  isAreaVisible as areaVisible,
  hidesInaccessibleAreas as roleHidesInaccessible,
  DEFAULT_ROLE,
  ROLES,
} from '../domain/permissions.js';

const session = ref(null);
const loading = ref(true);
let initialised = false;
/* Ist das Profil (und damit die Rolle) des aktuellen Nutzers geladen? Bis dahin fällt `role`
   auf die engste Rolle (guest) zurück — wer davon abhängig lädt (Spielerliste: Gäste lesen
   aus der öffentlichen Ansicht), muss warten (whenRoleResolved), sonst bekäme ein Admin kurz
   den Gast-Lesepfad. */
const profileSynced = ref(false);
/* Adressteil vor "?" beim Start — für ?code= und ?token_hash= (F11). Gleich beim Laden des Moduls gelesen,
   bevor die Auth-Bibliothek die Adresse anfasst. */
const INITIAL_SEARCH = typeof window !== 'undefined' ? window.location.search || '' : '';
/* Hinweis zu einem Mail-Link, der nicht funktioniert hat: '' | 'expired' | 'invalid'. Der Login zeigt ihn an. */
const linkProblem = ref('');
/* Link mit ?token_hash= erfolgreich eingelöst — dann gilt die Sitzung als aus einem Einladungslink (Passwort setzen). */
const redeemedByToken = ref(false);

export function useAuth() {
  const {
    profileName, profileAvatarPath, profileRole,
    loadProfile, resetProfile,
  } = useProfile();

  const user = computed(() => session.value?.user || null);
  const isSignedIn = computed(() => !!user.value);
  const email = computed(() => user.value?.email || '');

  /**
   * Anzeigename. Erste Wahl ist der selbst gesetzte Name aus `profiles` —
   * er laesst sich in der App aendern. Danach kommen die Nutzermetadaten aus
   * der Einladung, zuletzt der Teil der E-Mail-Adresse vor dem @.
   */
  const displayName = computed(() => {
    if (profileName.value) return profileName.value;
    const meta = user.value?.user_metadata || {};
    return meta.full_name || meta.name || email.value.split('@')[0] || '';
  });

  /** Pfad zum eigenen Profilbild im geschuetzten Speicher; '' wenn keins da. */
  const avatarPath = computed(() => profileAvatarPath.value);

  /**
   * Rolle des angemeldeten Nutzers, in dieser Reihenfolge:
   *
   *   1. `profiles.role` — die vorgesehene Quelle. An diese Tabelle kommt der
   *      Nutzer selbst nicht heran, und die Datenbankregeln werten sie direkt
   *      aus. Siehe supabase/profiles.sql.
   *   2. `app_metadata.role` — sicher, aber nur mit dem Serverschluessel
   *      setzbar. Uebergang/Rueckfall, falls eine Sitzung einmal ohne
   *      `profiles`-Zeile dasteht.
   *
   * Der fruehere dritte Schritt (`user_metadata.role`, vom Nutzer selbst
   * setzbar — auch auf "admin") ist entfernt: Alle Konten haben inzwischen
   * eine `profiles`-Zeile mit korrekter Rolle (RLS-Audit 2026-09-24, siehe
   * TODO.md "Unsicheren Zweig aus dem Rollen-Check entfernen" und
   * TODO_DONE.md). Sein Entfernen davor haette echte Admins ausgesperrt.
   *
   * Zur Einordnung: Beide verbliebenen Schritte sind nur Oberflaechen-
   * steuerung. Der wirksame Schutz liegt in den Datenbankregeln.
   */
  const role = computed(() => {
    if (!user.value) return null;
    if (ROLES.includes(profileRole.value)) return profileRole.value;

    const app = user.value.app_metadata || {};
    if (ROLES.includes(app.role)) return app.role;
    if (app.role === 'admin') return 'admin';

    return DEFAULT_ROLE;
  });

  const isAdmin = computed(() => role.value === 'admin');

  /** Darf der angemeldete Nutzer das? Siehe domain/permissions.js. */
  function can(area, action = 'read') {
    if (!isSignedIn.value) return false;
    return canDo(role.value, area, action);
  }

  /** Darf der angemeldete Nutzer Spieler endgültig löschen? Siehe canDeletePlayers in domain/permissions.js. */
  function canDeletePlayers() {
    if (!isSignedIn.value) return false;
    return canDeletePlayersFor(role.value);
  }

  /** Darf der angemeldete Nutzer Dateien endgültig löschen? Siehe canDeleteFiles in domain/permissions.js. */
  function canDeleteFiles() {
    if (!isSignedIn.value) return false;
    return canDeleteFilesFor(role.value);
  }

  /**
   * Soll der Bereich überhaupt angezeigt werden? Nicht dasselbe wie can():
   * Gesperrte Spielerbereiche bleiben sichtbar und erklären sich, Bereiche
   * der Vereinsverwaltung verschwinden ganz.
   */
  function isVisible(area) {
    if (!isSignedIn.value) return false;
    return areaVisible(role.value, area);
  }

  /**
   * Verschwinden Bereiche/Aktionen ohne Zugriff für die aktuelle Rolle ganz,
   * statt gesperrt und erklärt angezeigt zu werden? Siehe
   * HIDE_INACCESSIBLE_AREAS_FOR in domain/permissions.js.
   */
  const hidesInaccessibleAreas = computed(() => roleHidesInaccessible(role.value));

  /**
   * Darf dieser konkrete Eintrag geändert werden?
   *
   * Schreibrecht allein genügt nicht: Wer nicht der Geschäftsführung angehört,
   * darf nur ändern, was er selbst angelegt hat. So war es in der
   * ursprünglichen Fassung, und es ist bei mehreren Betreuern sinnvoll —
   * niemand überschreibt versehentlich die Notiz eines Kollegen.
   *
   * Einträge ohne Ersteller stammen aus der Zeit vor dieser Kennzeichnung.
   * Sie bleiben der Geschäftsführung vorbehalten.
   */
  function canManage(entry, area) {
    if (!can(area, 'write')) return false;
    if (role.value === 'admin') return true;
    const owner = entry?.createdBy || entry?.createdById || '';
    return !!owner && (owner === email.value || owner === user.value?.id);
  }

  /** Kennzeichnung für neue Einträge — Grundlage für canManage(). */
  function ownerStamp() {
    return {
      createdBy: email.value || user.value?.id || '',
      createdByName: displayName.value || '',
    };
  }

  async function init() {
    if (initialised) return;
    initialised = true;

    if (!isConfigured) {
      loading.value = false;
      return;
    }

    const link = parseAuthLink(INITIAL_HASH, INITIAL_SEARCH);
    if (link.kind === 'tokenHash') {
      /* Eigene Mailvorlage: Token einlösen, bevor die Sitzung gelesen wird. */
      const { error } = await supabase.auth.verifyOtp({ token_hash: link.tokenHash, type: link.type });
      if (error) {
        console.error('[auth] Link konnte nicht eingelöst werden:', error.message);
        linkProblem.value = 'expired';
      } else {
        redeemedByToken.value = true;
      }
    }

    const { data } = await supabase.auth.getSession();
    session.value = data.session;
    /* Fehler-/?code=-Link ohne Sitzung: Meldung statt stummem Login. Wer schon angemeldet ist, bleibt ungestört. */
    if (!session.value && (link.kind === 'error' || link.kind === 'code')) linkProblem.value = link.problem;
    if (link.kind === 'error' || link.kind === 'code' || link.kind === 'tokenHash') {
      /* Token/Fehler gehören nicht in die Adresszeile (Lesezeichen, Weitergabe). */
      window.history.replaceState(null, '', window.location.pathname);
    }
    await syncProfile();
    loading.value = false;

    supabase.auth.onAuthStateChange((_event, next) => {
      const changed = next?.user?.id !== session.value?.user?.id;
      session.value = next;
      if (changed) {
        /* Signierte Links sind an den Nutzer gebunden — der naechste wuerde
           sonst mit den Links des vorigen arbeiten. */
        clearUrlCache();
        resetProfile();
        profileSynced.value = false;
      }
      syncProfile();
    });
  }

  /** Profil zur aktuellen Sitzung holen bzw. nach dem Abmelden verwerfen. */
  async function syncProfile() {
    if (!user.value) {
      resetProfile();
      profileSynced.value = false;
      return;
    }
    try {
      await loadProfile(user.value.id);
    } finally {
      /* Auch bei einem Fehler "geklärt": Die Rolle fällt dann auf app_metadata bzw. guest zurück
         (die sichere, engste Richtung) — warten bis in alle Ewigkeit wäre schlechter. */
      profileSynced.value = true;
    }
  }

  /**
   * Wartet, bis die Rolle des angemeldeten Nutzers feststeht (Profil geladen oder gescheitert).
   * Ohne Sitzung wartet sie, bis es eine gibt und deren Profil geladen ist.
   */
  function whenRoleResolved() {
    if (profileSynced.value) return Promise.resolve(role.value);
    return new Promise((resolve) => {
      const stop = watch(profileSynced, (ready) => {
        if (!ready) return;
        stop();
        resolve(role.value);
      });
    });
  }

  async function signIn(mail, password) {
    if (!isConfigured) throw new Error('notConnected');
    linkProblem.value = '';
    const { error } = await supabase.auth.signInWithPassword({
      email: mail,
      password,
    });
    if (error) {
      console.error('[auth] Anmeldung fehlgeschlagen:', error.message);
      throw new Error('genericError');
    }
  }

  async function signOut() {
    if (!isConfigured) return;
    await supabase.auth.signOut();
  }

  /** Zugangsanfrage stellen. input: { firstName, lastName, organization, email } — alle Pflicht. */
  async function requestAccess(input) {
    if (!isConfigured) throw new Error('notConnected');
    const checked = normalizeAccessRequest(input);
    if (!checked.ok) throw new Error('invalidInput');
    await submitAccessRequest(checked.value);
  }

  /** Neues Passwort setzen — nach Einladung, nach Zurücksetzen, oder im eigenen Konto. */
  async function updatePassword(password) {
    if (!isConfigured) throw new Error('notConnected');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      console.error('[auth] Passwort konnte nicht gesetzt werden:', error.message);
      throw new Error('saveFailed');
    }
  }

  /**
   * Zurücksetzen-Link per E-Mail anstoßen.
   *
   * Gibt absichtlich nie einen Fehler weiter, der etwas über die Adresse
   * verraet (existiert sie, existiert sie nicht) — nur ein echtes
   * Verbindungsproblem und das Mail-Limit (emailRateLimit, hängt nicht von der
   * Adresse ab) werden sichtbar. Sonst liesse sich die App missbrauchen,
   * um herauszufinden, welche Adressen registriert sind.
   */
  async function requestPasswordReset(mail) {
    if (!isConfigured) throw new Error('notConnected');
    const { error } = await supabase.auth.resetPasswordForEmail(mail, {
      redirectTo: window.location.origin,
    });
    if (error) {
      console.error('[auth] Zurücksetzen-Anfrage fehlgeschlagen:', error.message);
      /* Das Mail-Limit hängt nicht von der Adresse ab und verrät nichts über Konten — nur dieser Fall
         wird sichtbar, damit niemand vergeblich auf eine Mail wartet. */
      if (isEmailRateLimit(error)) throw new Error('emailRateLimit');
    }
  }

  /** Kam der Nutzer über einen Einladungs- oder Zurücksetzen-Link? */
  const hasInviteLink = computed(() => {
    if (redeemedByToken.value) return true;
    const h = INITIAL_HASH + ' ' + (typeof window !== 'undefined' ? window.location.hash : '');
    return h.includes('access_token') || h.includes('type=invite') || h.includes('type=recovery');
  });

  return {
    session, loading, user, isSignedIn, email, displayName, avatarPath,
    role, isAdmin, can, canDeleteFiles, canDeletePlayers, isVisible, hidesInaccessibleAreas, canManage, ownerStamp,
    whenRoleResolved,
    init, signIn, signOut, requestAccess, hasInviteLink, linkProblem, syncProfile,
    updatePassword, requestPasswordReset,
  };
}

/**
 * Prüft ein neues Passwort (Mindestlänge, Übereinstimmung).
 * Liefert einen i18n-Schlüssel bei Fehler, sonst null. Eigenständige
 * Funktion statt Teil der Composable, weil sie ohne Sitzung auskommt und
 * an zwei Stellen gebraucht wird (SetPasswordPanel, ProfileDialog).
 */
export function validateNewPassword(password, confirmPassword) {
  if (password.length < 8) return 'auth.setPassword.tooShort';
  if (password !== confirmPassword) return 'auth.setPassword.mismatch';
  return null;
}
